import React, { useEffect, useState } from 'react'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import {
   Badge,
   CircularProgress,
   TableBody,
   TableRow,
   Tooltip,
   Typography,
   useMediaQuery,
   useTheme,
} from '@mui/material'
import {
   StyledTable,
   StyledTableCell,
   StyledTableHead,
   StyledTableHeadCell,
   StyledTableRow,
} from 'shared/components/StyledTableComponents'
import ImageComponent from 'shared/components/ImageComponent'
import { useTranslation } from 'react-i18next'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { HelpOutline, SwapHorizontalCircleOutlined } from '@mui/icons-material'
import { ProductDetails } from '../ProductDetails/types'
import { fetchProductDetails } from '../ProductDetails/services'
import { BracketsType } from './types'
import { pl, en, de } from './locales'
import { StyledAvailabilityDot } from '../ProductsList/styles'
import { fetchProducts } from './services'

const brackets = ['ECONOMY', 'MEDIUM_QUALITY', 'HIGH_QUALITY', 'PREMIUM']

export default function Replacements() {
   const [product, setProduct] = useState<ProductDetails | undefined>()
   const [data, setData] = useState<BracketsType | undefined>()
   const [searchParams] = useSearchParams()
   const productUuid = searchParams.get('uuid')
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const { i18n, t } = useTranslation(['TRANSLATION', 'REPLACEMENTS'])
   i18n.addResourceBundle('pl-PL', 'REPLACEMENTS', pl)
   i18n.addResourceBundle('en-EN', 'REPLACEMENTS', en)
   i18n.addResourceBundle('de-DE', 'REPLACEMENTS', de)
   const navigate = useNavigate()

   function returnProductName(productIndex: string, productName?: string) {
      if (productName) {
         if (productName.length > 100) {
            return `${productName.slice(0, 100)}...`
         }
         return productName
      }
      return productIndex
   }

   function returnAvailabilityDot(availability: number) {
      switch (true) {
         case availability === 0:
            return <StyledAvailabilityDot color="red" />
         case availability === 1:
            return <StyledAvailabilityDot color="orange" />
         case availability === -1:
            return <StyledAvailabilityDot color="gray" />
         case availability > 1:
            return <StyledAvailabilityDot color="green" />
         default:
            return '-'
      }
   }

   function renderBracketDetails(bracketName: string) {
      const foundBracket = data?.brackets.find((bracket) => bracket.bracket === bracketName)
      if (!foundBracket) {
         return null
      }
      if (!isMobile) {
         return (
            <>
               <TableRow>
                  <StyledTableHeadCell
                     sx={{
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(33,147,224, 0.1)',
                        fontSize: '13px',
                        color: 'black',
                     }}
                     colSpan={6}
                  >
                     {t(`REPLACEMENTS:brackets.${foundBracket.bracket.toLowerCase()}`)}
                  </StyledTableHeadCell>
               </TableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.CHEAPEST?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.CHEAPEST.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:cheapest')}</StyledTableCell>
                  <StyledTableCell>{foundBracket.results.CHEAPEST?.productIndex ?? '-'} </StyledTableCell>
                  <StyledTableCell>
                     {returnProductName(
                        foundBracket.results.CHEAPEST?.productIndex || '-',
                        foundBracket.results.CHEAPEST?.productName
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.CHEAPEST?.producerName ? (
                        <Tooltip title={foundBracket.results.CHEAPEST?.producerName}>
                           <ImageComponent
                              producerUuid={foundBracket.results.CHEAPEST?.producerUuid}
                              filename={foundBracket.results.CHEAPEST?.producerName.toLowerCase()}
                              producerName={foundBracket.results.CHEAPEST?.producerName}
                           />
                        </Tooltip>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.CHEAPEST?.meanPrice !== null
                        ? foundBracket.results.CHEAPEST?.formattedMeanPrice
                        : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.CHEAPEST
                        ? returnAvailabilityDot(foundBracket.results.CHEAPEST.availability)
                        : ''}
                  </StyledTableCell>
               </StyledTableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.FAVOURITE?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.FAVOURITE.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'row',
                           width: '100%',
                           justifyContent: 'center',
                           alignItems: 'center',
                           gap: '2px',
                        }}
                     >
                        <Typography fontSize="11.375px">{t('REPLACEMENTS:favourite')}</Typography>
                        <Tooltip title={t('REPLACEMENTS:favouriteSelecting')}>
                           <HelpOutline color="primary" sx={{ '&:hover': { cursor: 'pointer' } }} />
                        </Tooltip>
                     </Box>
                  </StyledTableCell>
                  <StyledTableCell>{foundBracket.results.FAVOURITE?.productIndex ?? '-'}</StyledTableCell>
                  <StyledTableCell>
                     {returnProductName(
                        foundBracket.results.FAVOURITE?.productIndex || '-',
                        foundBracket.results.FAVOURITE?.productName
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.FAVOURITE?.producerName ? (
                        <Tooltip title={foundBracket.results.FAVOURITE?.producerName}>
                           <ImageComponent
                              producerUuid={foundBracket.results.FAVOURITE.producerUuid}
                              filename={foundBracket.results.FAVOURITE?.producerName.toLowerCase()}
                              producerName={foundBracket.results.FAVOURITE?.producerName}
                           />
                        </Tooltip>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.FAVOURITE?.meanPrice !== null
                        ? foundBracket.results.FAVOURITE?.formattedMeanPrice
                        : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.FAVOURITE
                        ? returnAvailabilityDot(foundBracket.results.FAVOURITE.availability)
                        : '-'}
                  </StyledTableCell>
               </StyledTableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.MOST_ORDERED?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.MOST_ORDERED.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:mostOrdered')}</StyledTableCell>
                  <StyledTableCell>{foundBracket.results.MOST_ORDERED?.productIndex ?? '-'}</StyledTableCell>
                  <StyledTableCell>
                     {returnProductName(
                        foundBracket.results.MOST_ORDERED?.productIndex || '-',
                        foundBracket.results.MOST_ORDERED?.productName
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.MOST_ORDERED?.producerName ? (
                        <Tooltip title={foundBracket.results.MOST_ORDERED?.producerName}>
                           <ImageComponent
                              producerUuid={foundBracket.results.MOST_ORDERED.producerUuid}
                              filename={foundBracket.results.MOST_ORDERED?.producerName.toLowerCase()}
                              producerName={foundBracket.results.MOST_ORDERED?.producerName}
                           />
                        </Tooltip>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.MOST_ORDERED?.meanPrice !== null
                        ? foundBracket.results.MOST_ORDERED?.formattedMeanPrice
                        : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.MOST_ORDERED
                        ? returnAvailabilityDot(foundBracket.results.MOST_ORDERED.availability)
                        : '-'}
                  </StyledTableCell>
               </StyledTableRow>
               {/* <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.BOOSTED?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.BOOSTED.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:boosted')}</StyledTableCell>
                  <StyledTableCell>{foundBracket.results.BOOSTED?.productIndex ?? '-'}</StyledTableCell>
                  <StyledTableCell>
                     {returnProductName(
                        foundBracket.results.BOOSTED?.productIndex || '-',
                        foundBracket.results.BOOSTED?.productName
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.BOOSTED?.producerName ? (
                        <Tooltip title={foundBracket.results.BOOSTED?.producerName}>
                           <ImageComponent
                              filename={foundBracket.results.BOOSTED?.producerName.toLowerCase()}
                              producerName={foundBracket.results.BOOSTED?.producerName}
                           />
                        </Tooltip>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.BOOSTED?.meanPrice !== 0 && foundBracket.results.BOOSTED?.meanPrice !== null
                        ? foundBracket.results.BOOSTED?.formattedMeanPrice || '-'
                        : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.BOOSTED?.availability
                        ? returnAvailabilityDot(foundBracket.results.BOOSTED?.availability)
                        : '-'}
                  </StyledTableCell>
               </StyledTableRow> */}
               <StyledTableRow>
                  <StyledTableCell colSpan={6}>
                     <ContainedButton
                        onClick={() =>
                           navigate(
                              `/dashboard/search?value=${productUuid}&index=${product?.index}&bracket=${foundBracket.bracket}&searchType=SUBSTITUTES`
                           )
                        }
                     >
                        {t('REPLACEMENTS:showAll')}
                     </ContainedButton>
                  </StyledTableCell>
               </StyledTableRow>
            </>
         )
      }
      if (isMobile) {
         return (
            <>
               <TableRow>
                  <StyledTableHeadCell
                     sx={{
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(33,147,224, 0.1)',
                        fontSize: '13px',
                        color: 'black',
                     }}
                     colSpan={3}
                  >
                     {t(`REPLACEMENTS:brackets.${foundBracket.bracket.toLowerCase()}`)}
                  </StyledTableHeadCell>
               </TableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.CHEAPEST?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.CHEAPEST.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:cheapest')}</StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.CHEAPEST ? (
                        <Box>
                           <Typography fontWeight="bold">
                              {foundBracket.results.CHEAPEST.productIndex.length > 30
                                 ? `${foundBracket.results.CHEAPEST.productIndex.slice(0, 30)}...`
                                 : foundBracket.results.CHEAPEST.productIndex}
                           </Typography>
                           <span>
                              {returnProductName(
                                 foundBracket.results.CHEAPEST.productIndex,
                                 foundBracket.results.CHEAPEST.productName
                              )}
                           </span>
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.CHEAPEST ? (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '4px',
                           }}
                        >
                           <Tooltip title={foundBracket.results.CHEAPEST.producerName}>
                              <ImageComponent
                                 height={20}
                                 producerUuid={foundBracket.results.CHEAPEST.producerUuid}
                                 width={isMobile ? '100px' : 'auto'}
                                 filename={foundBracket.results.CHEAPEST.producerName.toLowerCase()}
                                 producerName={foundBracket.results.CHEAPEST.producerName}
                              />
                           </Tooltip>
                           {foundBracket.results.CHEAPEST.meanPrice !== null
                              ? foundBracket.results.CHEAPEST.formattedMeanPrice
                              : '-'}
                           {foundBracket.results.CHEAPEST
                              ? returnAvailabilityDot(foundBracket.results.CHEAPEST.availability)
                              : '-'}
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
               </StyledTableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.FAVOURITE?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.FAVOURITE.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           flexDirection: 'row',
                           alignItems: 'center',
                           gap: '2px',
                        }}
                     >
                        <Typography fontSize="11.375px">{t('REPLACEMENTS:favourite')}</Typography>
                        <Tooltip title={t('REPLACEMENTS:favouriteSelecting')}>
                           <HelpOutline color="primary" sx={{ '&:hover': { cursor: 'pointer' } }} />
                        </Tooltip>
                     </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.FAVOURITE ? (
                        <Box>
                           <Typography fontWeight="bold">
                              {foundBracket.results.FAVOURITE.productIndex.length > 30
                                 ? `${foundBracket.results.FAVOURITE.productIndex.slice(0, 30)}...`
                                 : foundBracket.results.FAVOURITE.productIndex}
                           </Typography>
                           <span>
                              {returnProductName(
                                 foundBracket.results.FAVOURITE.productIndex,
                                 foundBracket.results.FAVOURITE.productName
                              )}
                           </span>
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.FAVOURITE ? (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                           }}
                        >
                           <Tooltip title={foundBracket.results.FAVOURITE.producerName}>
                              <ImageComponent
                                 producerUuid={foundBracket.results.FAVOURITE.producerUuid}
                                 height={20}
                                 width={isMobile ? '100px' : 'auto'}
                                 filename={foundBracket.results.FAVOURITE.producerName.toLowerCase()}
                                 producerName={foundBracket.results.FAVOURITE.producerName}
                              />
                           </Tooltip>
                           {foundBracket.results.FAVOURITE.meanPrice !== null
                              ? foundBracket.results.FAVOURITE.formattedMeanPrice
                              : '-'}
                           {foundBracket.results.FAVOURITE
                              ? returnAvailabilityDot(foundBracket.results.FAVOURITE.availability)
                              : '-'}
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
               </StyledTableRow>
               <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.MOST_ORDERED?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.MOST_ORDERED.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:mostOrdered')}</StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.MOST_ORDERED ? (
                        <Box>
                           <Typography fontWeight="bold">
                              {foundBracket.results.MOST_ORDERED.productIndex.length > 30
                                 ? `${foundBracket.results.MOST_ORDERED.productIndex.slice(0, 30)}...`
                                 : foundBracket.results.MOST_ORDERED.productIndex}
                           </Typography>
                           <span>
                              {returnProductName(
                                 foundBracket.results.MOST_ORDERED.productIndex,
                                 foundBracket.results.MOST_ORDERED.productName
                              )}
                           </span>
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.MOST_ORDERED ? (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                           }}
                        >
                           <Tooltip title={foundBracket.results.MOST_ORDERED.producerName}>
                              <ImageComponent
                                 height={20}
                                 producerUuid={foundBracket.results.MOST_ORDERED.producerUuid}
                                 width={isMobile ? '100px' : 'auto'}
                                 filename={foundBracket.results.MOST_ORDERED.producerName.toLowerCase()}
                                 producerName={foundBracket.results.MOST_ORDERED.producerName}
                              />
                           </Tooltip>
                           {foundBracket.results.MOST_ORDERED.meanPrice !== null
                              ? foundBracket.results.MOST_ORDERED.formattedMeanPrice
                              : '-'}
                           {foundBracket.results.MOST_ORDERED
                              ? returnAvailabilityDot(foundBracket.results.MOST_ORDERED.availability)
                              : '-'}
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
               </StyledTableRow>
               <StyledTableRow>
                  <StyledTableCell colSpan={3}>
                     <ContainedButton
                        onClick={() =>
                           navigate(
                              `/dashboard/search?value=${productUuid}&index=${product?.index}&bracket=${foundBracket.bracket}&searchType=SUBSTITUTES`
                           )
                        }
                     >
                        {t('REPLACEMENTS:showAll')}
                     </ContainedButton>
                  </StyledTableCell>
               </StyledTableRow>
               {/* <StyledTableRow
                  onClick={() => {
                     if (foundBracket.results.BOOSTED?.productUuid) {
                        navigate(`/dashboard/product?uuid=${foundBracket.results.BOOSTED.productUuid}`)
                     }
                  }}
               >
                  <StyledTableCell>{t('REPLACEMENTS:boosted')}</StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.BOOSTED ? (
                        <Box>
                           <Typography fontWeight="bold">
                              {foundBracket.results.BOOSTED.productIndex.length > 30
                                 ? `${foundBracket.results.BOOSTED.productIndex.slice(0, 30)}...`
                                 : foundBracket.results.BOOSTED.productIndex}
                           </Typography>
                           <span>
                              {returnProductName(
                                 foundBracket.results.BOOSTED.productIndex,
                                 foundBracket.results.BOOSTED.productName
                              )}
                           </span>
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
                  <StyledTableCell>
                     {foundBracket.results.BOOSTED ? (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '4px',
                           }}
                        >
                           <Tooltip title={foundBracket.results.BOOSTED.producerName}>
                              <ImageComponent
                                 height={20}
                                 width={isMobile ? '100px' : 'auto'}
                                 filename={foundBracket.results.BOOSTED.producerName.toLowerCase()}
                                 producerName={foundBracket.results.BOOSTED.producerName}
                              />
                           </Tooltip>
                           {foundBracket.results.BOOSTED.meanPrice !== 0 &&
                           foundBracket.results.BOOSTED.meanPrice !== null
                              ? foundBracket.results.BOOSTED.formattedMeanPrice || '-'
                              : '-'}
                           {foundBracket.results.BOOSTED?.availability
                              ? returnAvailabilityDot(foundBracket.results.BOOSTED?.availability)
                              : '-'}
                        </Box>
                     ) : (
                        '-'
                     )}
                  </StyledTableCell>
               </StyledTableRow> */}
            </>
         )
      }
      return null
   }

   useEffect(() => {
      if (!searchParams.get('uuid')) {
         navigate('/dashboard')
      } else {
         fetchProductDetails(searchParams.get('uuid')!).then((res) => {
            setProduct(res)
            fetchProducts(searchParams.get('uuid')!).then((bracketsRes) => {
               if (!bracketsRes.brackets || bracketsRes.brackets.length === 0) {
                  navigate({
                     pathname: '/dashboard/search',
                     search: `?${createSearchParams({
                        value: productUuid!,
                        index: res.index,
                        searchType: 'SUBSTITUTES',
                     })}`,
                  })
               } else {
                  setData(bracketsRes)
               }
            })
         })
      }
   }, [searchParams])

   return (
      <Box>
         {product && data ? (
            <Box>
               <Typography variant="h5" color="primary">
                  {t('REPLACEMENTS:replacementsFor', { index: product.index })}
               </Typography>
               <Box sx={{ mt: { lg: '16px', md: '8px', xs: '8px' } }}>
                  <Box sx={{ mb: { lg: '16px', md: '12px', xs: '8px' }, display: 'flex', justifyContent: 'flex-end' }}>
                     <Badge badgeContent="BETA" color="error">
                        <ContainedButton
                           onClick={() =>
                              navigate({
                                 pathname: '/dashboard/search',
                                 search: `?${createSearchParams({
                                    value: productUuid!,
                                    index: product.index!,
                                    searchType: 'SUBSTITUTES',
                                 })}`,
                              })
                           }
                           variant="contained"
                        >
                           <SwapHorizontalCircleOutlined sx={{ color: 'white', marginRight: 2 }} />
                           {t('REPLACEMENTS:showAllSubstitutes')}
                        </ContainedButton>
                     </Badge>
                  </Box>
                  <StyledTable>
                     <StyledTableHead>
                        <StyledTableHeadCell />
                        {!isMobile && (
                           <StyledTableHeadCell>{t('TRANSLATION:columnNames.partIndex')}</StyledTableHeadCell>
                        )}
                        <StyledTableHeadCell>
                           {isMobile
                              ? t('TRANSLATION:columnNames.indexAndName')
                              : t('TRANSLATION:columnNames.productName')}
                        </StyledTableHeadCell>
                        {!isMobile && (
                           <StyledTableHeadCell>{t('TRANSLATION:columnNames.producer')}</StyledTableHeadCell>
                        )}
                        <StyledTableHeadCell>{t('TRANSLATION:columnNames.meanPrice')}</StyledTableHeadCell>
                        {!isMobile && (
                           <StyledTableHeadCell>{t('TRANSLATION:columnNames.availability')}</StyledTableHeadCell>
                        )}
                     </StyledTableHead>
                     <TableBody>{brackets.map((bracket) => renderBracketDetails(bracket))}</TableBody>
                  </StyledTable>
                  <Box sx={{ mt: { lg: '16px', md: '12px', xs: '8px' }, display: 'flex', justifyContent: 'flex-end' }}>
                     <Badge badgeContent="BETA" color="error">
                        <ContainedButton
                           onClick={() =>
                              navigate({
                                 pathname: '/dashboard/search',
                                 search: `?${createSearchParams({
                                    value: productUuid!,
                                    index: product.index!,
                                    searchType: 'SUBSTITUTES',
                                 })}`,
                              })
                           }
                           variant="contained"
                        >
                           <SwapHorizontalCircleOutlined sx={{ color: 'white', marginRight: 2 }} />
                           {t('REPLACEMENTS:showAllSubstitutes')}
                        </ContainedButton>
                     </Badge>
                  </Box>
               </Box>
            </Box>
         ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
               <CircularProgress />
            </Box>
         )}
      </Box>
   )
}
